import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { converter } from '@/lib/firestore-converters';
import { products as seedProducts } from '@/data';
import type { Product } from '@/types';

const USE_SEED = import.meta.env.VITE_USE_SEED_DATA === 'true';
const productsCol = () => collection(db, 'products').withConverter(converter<Product>());

async function fetchProducts(categorySlug?: string): Promise<Product[]> {
  if (USE_SEED) {
    const all = seedProducts.filter(p => p.active);
    return categorySlug ? all.filter(p => p.category === categorySlug) : all;
  }
  const col = productsCol();
  const q = categorySlug
    ? query(col, where('category', '==', categorySlug), where('active', '==', true))
    : query(col, where('active', '==', true));
  return (await getDocs(q)).docs.map(d => d.data());
}

export function useProducts(categorySlug?: string) {
  return useQuery({
    queryKey: ['products', categorySlug ?? 'all'],
    queryFn: () => fetchProducts(categorySlug),
    staleTime: 5 * 60_000,
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async (): Promise<Product> => {
      if (USE_SEED) {
        const found = seedProducts.find(p => p.slug === slug);
        if (!found) throw new Error('Product not found');
        return found;
      }
      const snap = await getDocs(query(productsCol(), where('slug', '==', slug), limit(1)));
      if (snap.empty) throw new Error('Product not found');
      return snap.docs[0].data();
    },
    enabled: Boolean(slug),
  });
}
